/**
 * Sub-aba 🧩 Matriz RACI da aba Outros (GUPM-192) — Matriz RACI Rev.1 (Out/2026),
 * seção 02 do documento de trabalho: aviso de rascunho, filtro por parte, tabela,
 * legenda e nota ¹. Dado estático do domínio (`@/domain/outros`), SEM PII — os
 * pontos focais ficam na sub-aba Contatos.
 *
 * Quem decide se esta tela aparece é o shell (`podeVerMatrizRaci`): operador não vê.
 */
import { useState } from 'react';
import {
  MATRIZ_RACI_AVISO,
  MATRIZ_RACI_NOTA_DIAGNOSTICO,
  MATRIZ_RACI_NOTA_RODAPE,
  PARTES_RACI,
  RACI_LEGENDA,
  TOTAL_ATIVIDADES_RACI,
  filtrarMatrizPorParte,
  type ParteRaciId,
  type RaciLetra,
} from '@/domain/outros';
import MatrizRaciTabela, { CelulaRaci } from './MatrizRaciTabela';

const LETRAS: readonly RaciLetra[] = ['R', 'A', 'C', 'I'];

const chipClasse = (ativo: boolean) =>
  `rounded-full border px-2.5 py-0.5 text-[11px] font-bold transition ${
    ativo ? 'border-jf-yellow bg-jf-yellow/15 text-jf-yellow' : 'border-jf-border text-jf-muted hover:text-white'
  }`;

export default function MatrizRaci() {
  const [parte, setParte] = useState<ParteRaciId | null>(null);
  const secoes = filtrarMatrizPorParte(parte);
  const visiveis = secoes.reduce((n, s) => n + s.atividades.length, 0);
  const nomeParte = PARTES_RACI.find((p) => p.id === parte)?.nome;
  // "TECAL (TECAL)" não: o nome só entra quando diz algo além da sigla.
  const rotuloParte = nomeParte && nomeParte !== parte ? `${parte} (${nomeParte})` : parte;

  return (
    <div className="min-w-0 space-y-3">
      <div
        role="note"
        className="rounded-lg border border-jf-yellow/40 bg-jf-yellow/10 px-3 py-2 text-xs font-semibold text-jf-yellow"
      >
        <span aria-hidden>⚠️ </span>
        {MATRIZ_RACI_AVISO}
      </div>

      <p className="text-xs text-jf-muted">
        Quem executa (R), quem aprova e presta contas (A), quem é consultado (C) e quem é informado (I) em
        cada atividade do CMG. Os pontos focais de cada parte estão na sub-aba Contatos.
      </p>

      <div role="group" aria-label="Filtrar por parte" className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-[11px] text-jf-dim">Parte (R ou A):</span>
        <button type="button" aria-pressed={parte === null} onClick={() => setParte(null)} className={chipClasse(parte === null)}>
          Todas
        </button>
        {PARTES_RACI.map((p) => (
          <button
            key={p.id}
            type="button"
            title={p.nome}
            aria-pressed={parte === p.id}
            onClick={() => setParte(p.id)}
            className={chipClasse(parte === p.id)}
          >
            {p.id}
          </button>
        ))}
      </div>

      <p className="text-[11px] text-jf-dim" aria-live="polite">
        {parte === null
          ? `${TOTAL_ATIVIDADES_RACI} atividades · ${PARTES_RACI.length} partes`
          : `${visiveis} de ${TOTAL_ATIVIDADES_RACI} atividades em que ${rotuloParte} é R ou A`}
      </p>

      <MatrizRaciTabela secoes={secoes} destaque={parte} />

      <ul aria-label="Legenda" className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-jf-muted">
        {LETRAS.map((l) => (
          <li key={l} className="flex items-center gap-1.5">
            <CelulaRaci letra={l} rotular={false} />
            <span>{RACI_LEGENDA[l].rotulo}</span>
          </li>
        ))}
      </ul>

      <div className="space-y-1 text-[11px] text-jf-dim">
        <p>
          <span className="font-bold text-jf-muted">Nota: </span>
          {MATRIZ_RACI_NOTA_DIAGNOSTICO}
        </p>
        <p>{MATRIZ_RACI_NOTA_RODAPE}</p>
        <p>
          Partes: {PARTES_RACI.map((p) => `${p.id} ${p.nome}`).join(' · ')}.
        </p>
      </div>
    </div>
  );
}
